import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import { type DocumentNode } from 'graphql';
import React, { type FC, useState } from 'react';
import {
  type SubmitHandler,
  useForm,
  type UseFormGetValues,
} from 'react-hook-form';

import {
  type EntityReviewInput,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { getFirstErrorMessage } from '../../utils/apollo';
import { DEFAULT_ERROR_MESSAGE } from '../../utils/constants';
import { wasMutationSuccessful } from '../../utils/mutations';
import { ErrorMessage, Textarea } from '../forms/Inputs';

import { ReviewDimension } from './ReviewDimension';
import { useBootcampSessionReviewFormCreateOrUpdateEntityReviewMutation } from './__generated-gql-types__/BootcampSessionReviewForm.generated';

const REGISTER_OPTIONS = Object.freeze({
  RATE: (getValues: UseFormGetValues<EntityReviewInput>) => ({
    validate: () => {
      const rate = getValues('rate');
      if (!rate || rate === 0) {
        return 'Overall rating is required';
      }
    },
  }),
});

interface BootcampSessionReviewFormProps {
  bootcampSessionUrn: Scalars['CoachingSessionUrn']['output'];
  categoryUrn: Scalars['CategoryUrn']['output'];
  defaultValues?: EntityReviewInput;
  onSuccess?: () => void;
  onRequestClose: () => void;
  refetchQueries?: DocumentNode[];
}

export const BootcampSessionReviewForm: FC<BootcampSessionReviewFormProps> = ({
  bootcampSessionUrn,
  categoryUrn,
  defaultValues,
  onSuccess,
  onRequestClose,
  refetchQueries,
}) => {
  const { showToast } = useToast();
  const [createOrUpdateReview, { loading: createReviewInFlight }] =
    useBootcampSessionReviewFormCreateOrUpdateEntityReviewMutation({
      refetchQueries: refetchQueries ?? undefined,
    });

  const formMethods = useForm<EntityReviewInput>({
    defaultValues: {
      ...defaultValues,
      entityUrn: bootcampSessionUrn,
      categoryUrn,
    },
  });

  const { register, watch, setValue, getValues, trigger, handleSubmit } =
    formMethods;

  register('rate', REGISTER_OPTIONS.RATE(getValues));
  register('bootcampSessionReview.contentRate');
  register('bootcampSessionReview.instructorRate');

  const watchRate = watch('rate');

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

  const onSubmitReview = async (
    values: EntityReviewInput,
    closeOnSave?: boolean,
  ) => {
    setErrorMessage(null);

    try {
      const res = await createOrUpdateReview({
        variables: {
          input: values,
        },
      });

      if (wasMutationSuccessful(res)) {
        showToast({
          type: ToastType.SUCCESS,
          message: 'Review submitted successfully',
          duration: ToastDuration.LONG,
        });
        onSuccess?.();

        if (closeOnSave) {
          onRequestClose();
        }
      } else {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        console.warn(getFirstErrorMessage(res.errors));
      }
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      console.warn(error);
    }
  };

  const onSubmitWrapper: SubmitHandler<EntityReviewInput> = async (values) => {
    await onSubmitReview(values);
  };

  return (
    <form
      className="flex grow flex-col overflow-hidden"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <div
        id="bootcamp-review-modal-content"
        className="flex grow flex-col justify-start overflow-y-auto px-6 py-8"
      >
        <h2 className="text-3xl font-medium leading-8 text-leland-gray-dark">
          Help us make bootcamps even better.
        </h2>
        <div className="mt-6">
          <ReviewDimension
            title="Overall"
            description="How would you rate the overall quality of this session?"
            rate={watchRate ?? 0}
            onRateChange={async (rate) => {
              setValue('rate', rate);
              await trigger('rate');
            }}
          />
          <ReviewDimension
            title="Content Quality"
            description="How would you rate the quality and relevance of the bootcamp content?"
            rate={watch('bootcampSessionReview.contentRate') ?? 0}
            onRateChange={async (rate) => {
              setValue('bootcampSessionReview.contentRate', rate);
              await trigger('bootcampSessionReview.contentRate');
            }}
          />
          <ReviewDimension
            title="Instructor Quality"
            description="How would you rate the instructor's knowledge and teaching ability?"
            rate={watch('bootcampSessionReview.instructorRate') ?? 0}
            onRateChange={async (rate) => {
              setValue('bootcampSessionReview.instructorRate', rate);
              await trigger('bootcampSessionReview.instructorRate');
            }}
          />
        </div>

        <Textarea
          className="mt-6"
          label="Additional feedback"
          labelClass="font-medium"
          subLabel="Do you have any feedback or suggestions to help us improve future sessions?"
          flag="Optional"
          flagClass="ml-0 text-leland-gray-extra-light font-normal"
          useFlagDivider
          placeholder="Write your feedback here..."
          register={register('comment')}
          rows={4}
        />
      </div>

      <div className="flex w-full flex-col items-center border-t border-leland-gray-stroke p-4 md:items-end md:rounded-b-lg">
        <div className="grid w-full grid-cols-1 items-center gap-2 md:flex">
          <Button
            label="Back"
            size={ButtonSize.LARGE}
            width={ButtonWidth.FULL_MOBILE}
            onClick={onRequestClose}
            id="bootcamp_session_review_form--back"
          />
          <div className="flex items-center justify-end space-x-4 md:grow">
            <Button
              type="submit"
              label="Submit"
              buttonColor={ButtonColor.PRIMARY}
              size={ButtonSize.LARGE}
              disabled={createReviewInFlight}
              id="review_form--continue_or_save"
            />
          </div>
        </div>
        {errorMessage ? (
          <div className="flex items-center justify-end">
            <ErrorMessage errorMessage={errorMessage} />
          </div>
        ) : null}
      </div>
    </form>
  );
};
